<script setup lang="ts">
import { onMounted,Ref,inject, ref, watch, computed } from 'vue'
import { Bar } from 'vue-chartjs'
import { sendGraphQLQuery } from '@/services/gql-util'
import type { ChartData } from 'chart.js'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

ChartJS.register( Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

// Define the possible data formats
interface TimeSeriesData {
	name: string
	total: number
}

interface CustomChartData {
	  age: number;
}
interface UploadRecord {
	RealDate: string;
	NumUploads: number;
}

const chartData = ref<ChartData<'bar'>>({
	labels: [],
	datasets: []
})
const vhostId = inject<Ref<string> | null>('vhostId', null);
function getCssVar(name: string): string {
	const style = getComputedStyle(document.documentElement)
	return style.getPropertyValue(name)?.trim() || ''
}

function hslVar(name: string, fallback: string = '#3b82f6'): string {
	const value = getCssVar(name)
	return value && /^\d+(\s+\d+%){2}$/.test(value) ? `hsl(${value})` : fallback
}

const props = withDefaults(defineProps<{
	  size?: 'small' | 'medium' | 'large';
	  height?: number;
	  data?: CustomChartData;
}>(), {
	  data: () => ({ age: 30 }),
});
onMounted(async () => {
	if(vhostId) {
		fetchUploads();
	}
});
const data = ref<TimeSeriesData[]>([]);
const reportTotal = ref(0)
const contentCount = ref(0)
const age = computed(() => props.data?.age ?? 30);
const divHeight = computed(() => {
	if (props.height) {
		if (props.size === 'small') {
			return (props.height * 100) + 'px';
		} else if (props.size === 'medium') {
			return (props.height * 200) + 'px';
		} else if (props.size === 'large') {
			return (props.height * 400) + 'px';
		}
	}
	return '100px';
});
const fetchUploads = async () => {
	try {
		if(vhostId) {
			const startDate = new Date(Date.now() - age.value * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
			const endDate = new Date().toISOString().split('T')[0]

			const query = `
				query ($vhost: Int!, $startDate: String, $endDate: String) {
					Vhost {
						getFileUploads(vhost: $vhost, startDate: $startDate, endDate: $endDate) {
							RealDate
							NumUploads
						}
					}
				}
			`
			const result = await sendGraphQLQuery<{ Vhost: { getFileUploads: UploadRecord[] } }>(query, {
				vhost: Number(vhostId.value),
				startDate,
				endDate,
			})

			const records = result?.Vhost?.getFileUploads || []
			if (records.length > 0) {
				const uploads = records.map((record) => {
					reportTotal.value += record.NumUploads ?? 0;
					const date = new Date(record.RealDate);
					    const dayOfMonth = date.getDate();
					    return {
						            name: dayOfMonth.toString(),
						            total: record.NumUploads
						        };
				});

				// Format data for Chart.js
				chartData.value = {
					labels: uploads.map(u => u.name),
					datasets: [{
						label: 'Uploads',
						data: uploads.map(u => u.total),
						backgroundColor:  hslVar('--primary'),
						borderColor:  hslVar('--ring'),
						hoverBackgroundColor:  hslVar('--primary-foreground'),
						hoverBorderColor:  hslVar('--primary'),
						borderWidth: 1,

					}]
				};

				contentCount.value = uploads.length;
			}
		}
	} catch (error) {
		console.error('Error fetching uploads:', error);
		if (error instanceof Error) {
			console.error('Error message:', error.message);
		}
	}
};

// Chart options based on size
const fontSizes = {
	small: 8,
	medium: 10,
	large: 12
}

const chartOptions = ref({
	responsive: true,
	maintainAspectRatio: false,
	plugins: {
		legend: {
			display: false
		}
	},
	scales: {
		y: {
			beginAtZero: true,
			ticks: {
				font: {
					size: fontSizes[props.size || 'large']
				}
			}
		},
		x: {
			ticks: {
				font: {
					size: fontSizes[props.size || 'large']
				}
			}
		}
	}
})
</script>

<template>
	<Card>
	<CardContent :style="'height:'+divHeight" class="p-2">
	<h2 class="text-body font-light">Uploads in last {{age}} days</h2>
		<Bar :data="chartData" :options="chartOptions"/>
	</CardContent>
	</Card>
</template>
